import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addMemberToOrganization,
  AddMemberToOrganizationRequest,
} from '@/apis/addMemberToOrganization';
import { useToast } from '@/hooks/useToast';

export const useAddMemberToOrganization = (
  organizationUUID: string | undefined
) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (request: AddMemberToOrganizationRequest) => {
      return await addMemberToOrganization(request);
    },
    onSuccess: () => {
      // Invalidate seat availability cache
      queryClient.invalidateQueries({
        queryKey: ['seatAvailability', organizationUUID],
      });

      // Invalidate user-related queries
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'all'] });

      // Invalidate organization data
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['organization-stats'] });

      // Email invites mint/reuse the inviter's invite token on the backend —
      // refetch so the link section shows the same link the email carries.
      queryClient.invalidateQueries({ queryKey: ['inviteToken'] });
      queryClient.invalidateQueries({ queryKey: ['inviteTokenUses'] });

      // Success toast is the caller's job — batch sends would spam one
      // toast per email from here.
    },
    onError: (error) => {
      toast({
        title: "Couldn't send invite",
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};
